import React, { useState } from 'react';
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import styles from "../../styles/common/UserSettings.module.scss";

import OTPInput from "../form-elements/OTPInput";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import Button from "../common/Button";

const TwoFactorVerificationSchema = Yup.object().shape({
  otp: Yup.string()
    .required("Verification code is required")
    .matches(/^\d{6}$/, "Must be exactly 6 digits")
});

interface TwoFactorVerificationFormProps {
  userId: number;
}

function TwoFactorVerificationForm({ userId }: TwoFactorVerificationFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { verify2FALogin } = useAuth();

  const handleSubmit = async (
    values: { otp: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      await verify2FALogin(values.otp, userId);
      setSuccess("Verification successful! Redirecting...");
      navigate('/'); // Redirect to home page after successful verification
    } catch (err: any) {
      let errorMessage = "Verification failed. Please try again.";
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ otp: "" }}
      validateOnMount={true}
      validateOnChange={true}
      validationSchema={TwoFactorVerificationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors, isValid, touched, dirty, values, setFieldValue }) => (
        <Form
          className="c-form"
          noValidate
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontSize: '0.875rem', 
              fontWeight: '500', 
              color: 'var(--brand-700)' 
            }}>
              Authentication Code
            </label>
            <OTPInput
              length={6}
              value={values.otp}
              onChange={(value) => setFieldValue('otp', value)}
              disabled={isSubmitting}
              hasError={!!(errors.otp && touched.otp)}
            />
          </div>

          {errors.otp && touched.otp && (
            <FormGroupErrorText text={errors.otp}/>
          )}

          {error && <FormGroupErrorText text={error}/>}
          {success && <div className={styles.successMessage}>{success}</div>}

          <Button
            type="submit"
            btnSize="lg"
            disabled={!isValid || isSubmitting || !dirty}
            isLoading={isLoading}
            isLoadingText="Verifying"
            text="Verify"
          />
        </Form>
      )}
    </Formik>
  );
}

export default TwoFactorVerificationForm;